import styles from "./ReservationDetails.module.scss";
import { useMemo } from "react";
import { EventDetailsContent } from "../../calendar/components/EventDetailsContent";
import { IMyReservationsViewModel } from "../models/MyReservationsViewModel";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import {
  callPhoneNumber,
  generateMapsLink,
} from "../../../../common/infrastructure/routes/routePaths";
import { useAppContext } from "../../../../AppContext";
import {
  IfFalse,
  IfTrue,
  RenderWhen,
} from "../../../../common/infrastructure/components/RenderWhen";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { IStudioSelectItemModel } from "../../../../common/infrastructure/models/studioSelectItemModel";
import { EventType, UserEventStatus } from "../../../../common/enums";
import { getEventDetailsContentWarningMessage } from "../../calendar/utils/portalCalendarUtility";
import { getEventCalendarTimeLabel } from "../../../../common/infrastructure/utilities/durationUtility";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { useCalendarDownload } from "../../../../common/infrastructure/hooks/useCalendarDownload";
import { WorkoutPlanButton } from "../../../../common/components/workoutPlanDialog/WorkoutPlanButton";

interface IReservationDetailsProps {
  items: IMyReservationsViewModel[];
  selectedReservationId?: number;
  canCancelReservations?: boolean;
  selectedStudio?: IStudioSelectItemModel;
  allowLateCancellationsWithPayment?: boolean;
  onEventReservationCancel: (reservationId?: number) => void;
  onOpenMusicDialog: (eventId: number) => void;
  hasReview?: boolean;
  onLeaveReview?: () => void;
}

export const ReservationDetails = ({
  items,
  selectedReservationId,
  canCancelReservations,
  selectedStudio,
  allowLateCancellationsWithPayment,
  onEventReservationCancel,
  onOpenMusicDialog,
  hasReview,
  onLeaveReview,
}: IReservationDetailsProps) => {
  const currentItem = useMemo(
    () => items.find((x) => x.reservationId === selectedReservationId),
    [selectedReservationId],
  );
  const { currentUser } = useAppContext();
  const { downloadCalendar, isDownloading } = useCalendarDownload();

  if (!selectedReservationId || !currentUser || !currentItem) {
    return;
  }

  const currentItemCanCancelReservations =
    canCancelReservations &&
    (!currentItem.eventDetails.isCancellationWindowClosed ||
      !!allowLateCancellationsWithPayment);

  // A payment method alone (e.g. Multisport) still lets the client cancel; a
  // paid reservation is cancelled by the studio.
  const canCancelThisReservation =
    !!currentItemCanCancelReservations &&
    currentItem.eventDetails.type !== EventType.Individual &&
    !currentItem.isPaid;

  const isLateCancellation =
    canCancelReservations &&
    currentItem.eventDetails.isCancellationWindowClosed &&
    !!allowLateCancellationsWithPayment;

  const handleCancelClick = () => {
    if (isLateCancellation) {
      showCustomConfirmDialog(
        "Тази резервация е извън периода за безплатно отказване. Ако решите да я откажете, ще трябва да заплатите таксата чрез банков превод или на място при следващото си посещение.\n\nСигурни ли сте, че искате да откажете резервацията?",
        () => onEventReservationCancel(currentItem.reservationId),
        () => {},
        "warning",
        "Да",
        "Не",
      );
    } else {
      showCustomConfirmDialog(
        "Сигурни ли сте, че искате да отмените резервацията си?",
        () => onEventReservationCancel(currentItem.reservationId),
        () => {},
        "warning",
        "Да",
        "Не",
      );
    }
  };

  const warningMessage = getEventDetailsContentWarningMessage({
    isPassed: currentItem.eventDetails.isPassed,
    eventType: currentItem.eventDetails.type,
    canCancelReservations: currentItemCanCancelReservations,
    isCancellationWindowClosed:
      currentItem.eventDetails.isCancellationWindowClosed,
    allowLateCancellationsWithPayment: allowLateCancellationsWithPayment,
    isPaid: currentItem.isPaid,
    status: currentItem.status,
  });

  return (
    <div className={styles.root}>
      <EventDetailsContent
        eventName={currentItem?.eventDetails?.name}
        reservationId={currentItem.reservationId}
        instructor={currentItem?.eventDetails?.instructor}
        dateLabel={dateUtility.formatDate(currentItem?.eventDetails?.date)}
        timeLabel={getEventCalendarTimeLabel({
          startTime: currentItem?.eventDetails?.startTime,
          endTime: currentItem?.eventDetails?.endTime,
          durationInMinutes: currentItem?.eventDetails?.durationInMinutes,
        })}
        address={currentItem?.eventDetails?.studio.address}
        studioName={currentItem.eventDetails.studio.name}
        mapsLink={
          generateMapsLink(
            currentItem?.eventDetails?.studio?.address,
            currentItem?.eventDetails?.studio?.addressMapUrl,
          ) ?? ""
        }
        phoneNumber={currentItem?.eventDetails?.studio?.phoneNumber}
        secondaryPhoneNumber={
          currentItem?.eventDetails?.studio?.secondaryPhoneNumber
        }
        description={currentItem?.eventDetails?.description}
        paymentType={currentItem.paymentType}
        pricePaid={
          !!currentItem.paymentPlanId ? undefined : currentItem.pricePaid
        }
        currencyId={currentItem.currencyId}
        numberOfUserVisit={currentItem.visitsUsed}
        totalPlanVisits={currentItem.initialPlanVisits}
        paymentPlanId={currentItem.paymentPlanId}
        paymentPlanName={currentItem.planName}
        reservations={[
          {
            userId: currentItem.userId,
            userName: currentItem.userName,
            status: currentItem.status,
            isCancelled: currentItem.isCancelled,
            reservationId: currentItem.reservationId,
            type: currentItem.eventDetails.type,
          },
        ]}
        isCancelled={currentItem.isCancelled}
        status={currentItem.status}
        warningMessage={warningMessage}
        hallName={currentItem.eventDetails.hallName}
        price={currentItem?.eventDetails?.price}
        isPassed={currentItem.eventDetails.isPassed}
        isRemote={currentItem.eventDetails.isRemote}
        meetingUrl={currentItem.eventDetails.meetingUrl}
      />

      {/* Add to Calendar Button */}
      <RenderWhen
        condition={
          !currentItem.eventDetails.isPassed &&
          !currentItem.isCancelled &&
          currentItem.status !== UserEventStatus.PenaltyCancelled &&
          currentItem.status !== UserEventStatus.WaitingList
        }
      >
        <div className={styles.buttons}>
          <DefaultButton
            onClick={() => downloadCalendar(currentItem.reservationId)}
            label="Добави в календара"
            design="outline"
            loading={isDownloading}
          />
        </div>
      </RenderWhen>

      <RenderWhen
        condition={
          !!onLeaveReview &&
          currentItem.status === UserEventStatus.Arrived &&
          !currentItem.isCancelled
        }
      >
        <div className={styles.buttons}>
          <DefaultButton
            onClick={() => onLeaveReview?.()}
            label={hasReview ? "Редактирай ревюто" : "Остави ревю"}
            design="accent"
          />
        </div>
      </RenderWhen>

      <RenderWhen
        condition={
          currentItem.eventDetails.type === EventType.Individual &&
          !currentItem.isCancelled &&
          !!currentItem.hasWorkoutPlan
        }
      >
        <div className={styles.buttons}>
          <WorkoutPlanButton
            eventId={currentItem.eventDetails.id}
            mode="user"
            variant="button"
            design="accent"
            registeredUserName={currentItem.userName}
            eventDateLabel={dateUtility.formatDate(currentItem.eventDetails.date)}
            eventTimeLabel={`${currentItem.eventDetails.startTime} - ${currentItem.eventDetails.endTime}`}
          />
        </div>
      </RenderWhen>

      {/* Music Suggestions Button - Only show when event allows it and hasn't passed */}
      <RenderWhen
        condition={
          !currentItem.eventDetails.isPassed &&
          currentItem.eventDetails.allowMusicSuggestions &&
          !currentItem.isCancelled &&
          currentItem.status !== UserEventStatus.PenaltyCancelled &&
          currentItem.status !== UserEventStatus.WaitingList
        }
      >
        <div className={styles.buttons}>
          <DefaultButton
            onClick={() => onOpenMusicDialog(currentItem.eventDetails.id)}
            label={"Предложи музика"}
            design="secondary"
          />
        </div>
      </RenderWhen>

      <RenderWhen
        condition={
          !currentItem.eventDetails.isPassed &&
          !currentItem.isCancelled &&
          currentItem.status !== UserEventStatus.PenaltyCancelled &&
          currentItem.status !== UserEventStatus.WaitingList
        }
      >
        <div className={styles.buttons}>
          <RenderWhen condition={canCancelThisReservation}>
            <IfTrue>
              <DefaultButton
                onClick={handleCancelClick}
                label={"Канселиране на резервацията"}
                design="outline"
              />
            </IfTrue>
            <IfFalse>
              <DefaultButton
                onClick={() => callPhoneNumber(selectedStudio?.phoneNumber)}
                label={"Свържи се с обекта"}
                design="link"
              />
            </IfFalse>
          </RenderWhen>
        </div>
      </RenderWhen>
    </div>
  );
};
